import {Octicon, Box, Text, Link} from '@primer/react'
import {useFragment} from 'react-relay/hooks'

import {getSourceIcon} from '../utils/get-source-icon'
import type {CrossReferencedEvent$key} from './__generated__/CrossReferencedEvent.graphql'
import {issueHovercardPath, issueLinkedPullRequestHovercardPath} from '@github-ui/paths'
import {CrossReferencedEventFragment} from './CrossReferencedEvent'
import {LockIcon} from '@primer/octicons-react'
import {LABELS} from '..'

type CrossReferencedEventInnerProps = {
  queryRef: CrossReferencedEvent$key
  issueUrl: string
  parentDatabaseId: number | null
}

export function CrossReferencedEventInner({queryRef}: CrossReferencedEventInnerProps): JSX.Element {
  const {databaseId, source, target} = useFragment(CrossReferencedEventFragment, queryRef)
  const isIssue = source.__typename === 'Issue'
  const isPullRequest = source.__typename === 'PullRequest'

  if (!isIssue && !isPullRequest) {
    return <></>
  }

  const state = isPullRequest ? source.state : undefined
  const stateReason = isIssue ? source.stateReason : undefined
  const isDraft = isPullRequest ? source.isDraft : undefined
  const isInMergeQueue = isPullRequest ? source.isInMergeQueue : undefined
  const icon = getSourceIcon(source.__typename, state, stateReason, isDraft, isInMergeQueue)

  const dataHovercardUrl =
    source.repository && source.number
      ? isIssue
        ? issueHovercardPath({
            repo: source.repository.name,
            owner: source.repository.owner.login,
            issueNumber: source.number,
          })
        : issueLinkedPullRequestHovercardPath({
            owner: source.repository.owner.login,
            repo: source.repository.name,
            pullRequestNumber: source.number,
          })
      : null

  const repoIssueText =
    source.repository.id === target.repository?.id
      ? `#${source.number}`
      : `${source.repository.owner.login}/${source.repository.name}#${source.number}`

  const privateDescriptionId = `${databaseId}-private-description`
  return (
    <Box
      as="li"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Octicon icon={icon.icon} size={16} sx={{color: icon.color}} />
      <Link
        sx={{color: 'fg.default'}}
        href={source.url}
        data-hovercard-url={dataHovercardUrl}
        aria-describedby={source.repository.isPrivate ? privateDescriptionId : undefined}
      >
        {source.title} <Text sx={{color: 'fg.muted'}}>{repoIssueText}</Text>
      </Link>
      {source.repository.isPrivate && (
        <>
          <Octicon icon={LockIcon} />
          <Text id={privateDescriptionId} className="sr-only">
            {LABELS.crossReferencedEvent.privateEventDescription}
          </Text>
        </>
      )}
    </Box>
  )
}

try{ CrossReferencedEventInner.displayName ||= 'CrossReferencedEventInner' } catch {}